import { Component, effect, inject, input, signal, untracked, viewChild } from '@angular/core';
import { ServicePaymentFormStore } from './service-payment-form.store';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AddressDetailObject,
    PmupDataInput,
    PuroDataInput,
    ServicePaymentGridItemObject,
    ServicePaymentObject,
} from '@it2go/types';
import { BillingPaymentTypeEnum } from '../../../../../../../../../types/src/services/enum/billing-payment-type.enum';
import {
    BillingTransportTypeEnum,
    billingTransportTypes,
} from '../../../../../../../../../types/src/services/enum/billing-transport-type.enum';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { mapAddress } from '../../../../../shared/helpers/map-address.helper';
import { SessionService } from '../../../../../../services/session.service';
import { FieldErrorFactory } from '../../../../../shared/service/field-error.service';
import { insuranceTypeEnumOptions } from '../../../../../../../../../types/src/common/enum/insurance-type.enum';
import { InputWysiwygComponent } from '../../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { fb, translate } from '../../../../../shared/shared.global-service';
import { contentGlobalSuccess } from '../../../../content.error';
import { changeSignal } from '../../../../../../utils/form.utils';
import { localDate } from '../../../../../shared/pipes/local-date.pipe';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import {
    InsuranceCompanySelectInputComponent,
} from '../../../../../../components/medical-service/element/insurance-company-select-input/insurance-company-select-input.component';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import {
    ErrorFloatMessageComponent,
} from '../../../../../../components/kit/float-message/error-float-message.component';
import { NumberInputComponent } from '../../../../../../components/kit/input/text-input/number-input.component';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input/check-input.component';
import {
    TextAreaInputComponent,
} from '../../../../../../components/kit/input/text-area-input/text-area-input.component';
import { AddressInputComponent } from '../../../../../../components/kit/input/address-input/address-input.component';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';

const defaultPuroDataInput: PuroDataInput = {
    payment: 0,
    clients: 0,
    highCostPayment: 0,
    puroRef: 0,
    direct: false,
    coefficients: [],
};

const defaultPmupDataInput: PmupDataInput = {
    payment: 0,
    pmupRef: 0,
    pumRef: 0,
    direct: false,
};

@Component({
    selector: 'app-service-payment-form',
    templateUrl: './service-payment-form.component.html',
    styleUrls: ['./service-payment-form.component.scss'],
    providers: [ServicePaymentFormStore],
    imports: [
        PrimeTemplate,
        TranslatePipe,
        SelectInputComponent,
        InsuranceCompanySelectInputComponent,
        DateInputComponent,
        TextInputComponent,
        ErrorFloatMessageComponent,
        NumberInputComponent,
        CheckInputComponent,
        InputWysiwygComponent,
        TextAreaInputComponent,
        AddressInputComponent,
        ActionButtonsComponent,
    ],
})
export class ServicePaymentFormComponent {

    protected readonly store = inject(ServicePaymentFormStore);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly session = inject(SessionService);

    public serviceId = input<number>();
    public updateId = input<number | null>(null);
    public copyId = input<number | null>(null);
    public puroForm = input<FormGroup | null>(null);
    public pmupForm = input<FormGroup | null>(null);

    public readonly wysiwyg = viewChild<InputWysiwygComponent>(InputWysiwygComponent);

    currentYear = new Date().getFullYear().toString();

    form = fb.group({
        insuranceCompanyId: fb.control<number | null>(null, Validators.required),
        insuranceType: fb.control<string | null>(null, Validators.required),
        validFrom: fb.control<Date | null>(new Date(this.currentYear + '-01-01 00:00:00'), Validators.required),
        validTo: fb.control<Date | null>(
            new Date(this.currentYear + '-12-31 23:59:59'),
            [Validators.required, this.validToAfterValidFromValidator()],
        ),
        serviceICZ: fb.control<string | null>(null, [Validators.required, Validators.maxLength(8)]),
        paymentType: fb.control<string | null>(BillingPaymentTypeEnum.PROCEDURES, Validators.required),
        paymentTypeAmount: fb.control<number | null>(null),
        transportType: fb.control<string | null>(null, Validators.required),
        paymentForTransport: fb.control<number | null>(null),
        dontBillVisitsWithNoDistance: fb.control<boolean>(false),
        reportTraveledDistanceToKdavka: fb.control<boolean>(true),
        priceForPoint: fb.control<number | null>(null, Validators.required),
        pointsPerMinute: fb.control<number | null>(null),
        maximalMonthlyPrepaymentLimit: fb.control<number | null>(null),
        address: fb.control<string | null>(null, Validators.required),
        note: fb.control<string | null>(null),
        invoiceDueInDays: fb.control<number | null>(14, Validators.required),
        pohodaInsuranceCompanyBranchName: fb.control<string | null>(null),
        pohodaInsuranceCompanyBranchAddress: fb.control<AddressDetailObject | null>(null),
    });

    transportTypes = billingTransportTypes;

    protected overlapErrorMessage = signal<string | null>(null);

    private insuranceCompanyId = changeSignal(this.form.controls.insuranceCompanyId);
    private insuranceType = changeSignal(this.form.controls.insuranceType);
    private validFrom = changeSignal(this.form.controls.validFrom);
    private validTo = changeSignal(this.form.controls.validTo);

    constructor() {
        effect(() => {
            if (this.copyId()) {
                this.loadServicePayment(this.copyId());
            } else if (this.updateId()) {
                this.loadServicePayment(this.updateId());
            } else if (this.serviceId()) {
                this.loadServiceBasicInfo(this.serviceId()!);
            }
        });

        effect(() => {
            const serviceBasicInfo = this.store.serviceBasicInfo();
            if (serviceBasicInfo?.territorialVzpWorkplace) {
                const workplace = serviceBasicInfo.territorialVzpWorkplace;
                this.form.patchValue({
                    serviceICZ: serviceBasicInfo.icz,
                    address: `${workplace.address2}<br />${workplace.zip} ${workplace.address1}`,
                });
            }
        });

        effect(() => {
            const serviceId = this.serviceId();
            const insuranceCompanyId = this.insuranceCompanyId();
            const insuranceType = this.insuranceType();
            const validFrom = this.validFrom();
            const validTo = this.validTo();
            if (serviceId && insuranceCompanyId && insuranceType && validFrom && validTo) {
                untracked(() => this.validateOverlap(validFrom, validTo).then()).then();
            }
        });

        effect(() => {
            if (this.validFrom()) {
                // trigger validator of validTo which checks validFrom <= validTo
                this.form.controls.validTo.markAsTouched();
                this.form.controls.validTo.updateValueAndValidity();
            }
        });

        effect(() => {
            const servicePayment = this.store.data.getServicePayment();
            if (!servicePayment) return;

            if (this.updateId()) {
                this.form.patchValue({
                    ...servicePayment,
                    validFrom: new Date(servicePayment.validFrom),
                    validTo: new Date(servicePayment.validTo),
                });
                this.puroForm()?.patchValue(servicePayment?.puro || {});
                this.pmupForm()?.patchValue(servicePayment?.pmup || {});
            } else {
                const nextYear = (new Date(servicePayment.validTo).getFullYear() + 1).toString();
                const newValidFrom = nextYear + '-01-01 00:00:00';
                const newValidTo = nextYear + '-12-31 23:59:59';
                this.form.patchValue({
                    ...servicePayment,
                    validFrom: new Date(newValidFrom),
                    validTo: new Date(newValidTo),
                });
            }
        })
    }

    async submit() {
        if (isInvalid(this.form)) return;

        await this.validateOverlap(this.validFrom()!, this.validTo()!);
        if (this.overlapErrorMessage()) return;

        const pohodaAddress = this.form.value.pohodaInsuranceCompanyBranchAddress;

        const _data = {
            puro: this.puroForm()?.getRawValue() || null,
            pmup: this.pmupForm()?.getRawValue() || null,
            validFrom: this.form.value.validFrom!.toISOString(),
            validTo: this.form.value.validTo!.toISOString(),
            serviceICZ: this.form.value.serviceICZ!,
            paymentType: this.form.value.paymentType || BillingPaymentTypeEnum.PROCEDURES,
            paymentTypeAmount: Number(this.form.value.paymentTypeAmount || 0),
            transportType: this.form.value.transportType!,
            paymentForTransport: this.form.value.transportType === BillingTransportTypeEnum.NONE ? 0 : Number(this.form.value.paymentForTransport || 0),
            dontBillVisitsWithNoDistance: this.form.value.dontBillVisitsWithNoDistance || false,
            reportTraveledDistanceToKdavka: this.form.value.reportTraveledDistanceToKdavka || false,
            priceForPoint: Number(this.form.value.priceForPoint || 0),
            pointsPerMinute: this.form.value.pointsPerMinute ?? null,
            maximalMonthlyPrepaymentLimit: Number(this.form.value.maximalMonthlyPrepaymentLimit) || null,
            address: this.form.value.address!,
            note: this.form.value.note || null,
            invoiceDueInDays: Number(this.form.value.invoiceDueInDays || 14),
            pohodaInsuranceCompanyBranchName: this.form.value.pohodaInsuranceCompanyBranchName || null,
            pohodaInsuranceCompanyBranchAddress: pohodaAddress ? mapAddress(pohodaAddress as AddressDetailObject) : null,
        };

        if (this.updateId()) {
            this.store.api.updateServicePayment({
                ..._data,
                id: this.updateId()!,
            }).then(() => {
                contentGlobalSuccess.next('common.updated');
                this.cancel();
            });
        } else {
            this.store.api.createServicePayment({
                ..._data,
                serviceId: this.serviceId()!,
                insuranceCompanyId: this.form.value.insuranceCompanyId!,
                insuranceType: this.form.value.insuranceType!,
            }).then(() => {
                contentGlobalSuccess.next('common.created');
                this.cancel();
            });
        }
    }

    cancel() {
        this.wysiwyg()?.resetBackupValues();

        this.router.navigate(['../'], { relativeTo: this.route });
    }

    private loadServicePayment(id: number | null) {
        if (id) {
            this.store.api.getServicePayment({ id });
        }
    }

    private loadServiceBasicInfo(serviceId: number) {
        const filter = new FilterBuilder();
        filter.where('serviceId', serviceId);
        filter.where('validFrom', new Date().toISOString(), 'LTE');
        filter.sort('validFrom', 'DESC');
        filter.limit(1);
        this.store.api.getServiceBasicInfos(filter.filter()).then();
    }

    private validToAfterValidFromValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value && this.form?.value?.validFrom && control.value < this.form.value.validFrom) {
                return FieldErrorFactory.invalidValidityPeriodError();
            }
            return null;
        };
    }

    private async validateOverlap(validFrom?: Date, validTo?: Date) {
        let overlapErrorMessage: string | null = null;
        const overlappingPayments = await this.loadServicePaymentsForOverlap(validFrom, validTo);
        if (overlappingPayments.length) {
            overlapErrorMessage = translate.instant(
                'service.payments.form.'
                + (overlappingPayments.length === 1 ? 'one' : 'multiple')
                + 'OverlapErrorMessage',
            );
            overlappingPayments.forEach((payment, index) => {
                overlapErrorMessage +=
                    (index > 0 ? ', ' : '')
                    + localDate(payment.validFrom, this.session, 'd.M.y')
                    + ' - '
                    + localDate(payment.validTo, this.session, 'd.M.y');
            });
        }
        this.overlapErrorMessage.set(overlapErrorMessage);
    }

    private async loadServicePaymentsForOverlap(validFrom?: Date, validTo?: Date): Promise<ServicePaymentGridItemObject[]> {
        const from = validFrom || this.validFrom()!;
        const to = validTo || this.validTo()!;

        const filter = new FilterBuilder();
        filter.where('serviceId', this.serviceId());
        filter.where('insuranceCompanyId', this.insuranceCompanyId());
        filter.where('insuranceType', this.insuranceType());
        filter.where('validFrom', to.toISOString(), 'LTE');
        filter.where('validTo', from.toISOString(), 'GTE');
        if (this.updateId()) {
            filter.where('id', this.updateId(), 'NEQ');
        }
        filter.sort('validFrom', 'ASC');
        filter.limit(10);

        const res = await this.store.api.getServicePayments(filter.filter(), `
            validFrom
            validTo
        `);
        return res?.['items'] || [];
    }

    protected readonly BillingTransportTypeEnum = BillingTransportTypeEnum;
    protected readonly insuranceTypeEnumOptions = insuranceTypeEnumOptions;

}
